import { api, readTotalCount } from '../api/client';
import { API_ITEM_CONFIRMATIONS_URL, API_ITEM_CONFIRMATION_URL, API_ITEM_CONFIRMERS_EXPORT_URL, API_ITEM_CONFIRMERS_URL } from '../utils';
import { slugify } from '../utils/strings';

/**
 * "ME TOO" — an item's confirmations (KPR-390; keepr-api SCHEMA.md
 * § Confirmations ("me too"), utils/confirmations.js).
 *
 * A `confirmations` element on a card holds HOW MANY people said "me too" on
 * the item (utils/confirmationValues.js). Each signed-in person adds or takes
 * back their OWN — nothing here names another account, so nobody can confirm
 * for someone else; everyone who reads the item sees the count; only the
 * collection's managers (and admins) see who.
 *
 * - `GET /api/items/{id}/confirmations` → `{ item_id, elements: [{ element,
 *   label, count, confirmed, canConfirm, canSeeWho }] }` — the caller's own
 *   state and the POST's own verdict, never who. An empty list when the card
 *   has none; 404 when the item cannot be read.
 * - `POST /api/items/{id}/confirmations/{element}` → `{ element, count,
 *   confirmed: true, changed }` (a repeat is `changed: false`).
 * - `DELETE` the same → `{ element, count, confirmed: false, changed }`. Never
 *   gated on a verified email, the collection allowing it, or the budgets'
 *   door alone: a person may always take back their own.
 *   Refusals keep `error.response.data` (`code`, `retryAfter`, `budget`) for
 *   the caller to word: 403 `email_unverified` · 403 `confirm_not_allowed` ·
 *   403 archived (a message only) · 404 `unknown_element` · 409
 *   `item_pending` · 429 `rate_limited` (`budget: 'toggle'` is the ten
 *   changes an hour per item).
 * - `GET …/{element}/people?skip&limit` → `{ element, total, skip, limit,
 *   people: [{ user: { _id, firstName, lastName, fullName, avatar } | null,
 *   confirmedAt }] }`, newest first. Managers and admins only (403
 *   `confirmations_managers_only`); never an email.
 * - `GET …/{element}/people/export` (KPR-440) → the same people as a CSV
 *   file, `Name,Confirmed on`, oldest first: the list's own gate (managers
 *   and admins; 403 `confirmations_managers_only`), plus a confirmed email
 *   (403 `email_unverified`, as every export). Made on request and never
 *   stored; the API writes one History row saying a manager took it.
 *   `X-Export-Truncated` / `X-Total-Count` past 100,000 people.
 *
 * A confirmation is not a content change: it moves no `updatedAt`, so the
 * caller pushes the new count into the item it holds in place (the
 * `onItemUpdated` pattern of hooks/useItemOwnTag.js), never as an edit.
 */

/** What the caller may know of an item's confirmations. */
export async function getItemConfirmations(itemId) {
	const { data } = await api.get(API_ITEM_CONFIRMATIONS_URL(itemId));
	return {
		item_id: data?.item_id || itemId,
		elements: Array.isArray(data?.elements) ? data.elements : [],
	};
}

/** Say "me too" on one element: `{ element, count, confirmed: true, changed }`. */
export async function confirmItem(itemId, element) {
	const { data } = await api.post(API_ITEM_CONFIRMATION_URL(itemId, element));
	return data || {};
}

/** Take back your own: `{ element, count, confirmed: false, changed }`. */
export async function withdrawConfirmation(itemId, element) {
	const { data } = await api.delete(API_ITEM_CONFIRMATION_URL(itemId, element));
	return data || {};
}

/** A page of who confirmed, newest first (managers only). */
export async function listConfirmers(itemId, element, { skip = 0, limit = 50 } = {}) {
	const { data } = await api.get(API_ITEM_CONFIRMERS_URL(itemId, element), { params: { skip, limit } });
	return {
		element: data?.element || element,
		total: Number.isFinite(data?.total) ? data.total : 0,
		skip: Number.isFinite(data?.skip) ? data.skip : skip,
		limit: Number.isFinite(data?.limit) ? data.limit : limit,
		people: Array.isArray(data?.people) ? data.people : [],
	};
}

// jsdom (and older Safari) have no Blob.text(); FileReader is the floor.
const readBlobText = blob => {
	if (typeof blob?.text === 'function') return blob.text();
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(`${reader.result || ''}`);
		reader.onerror = () => reject(reader.error || new Error('Unable to read the answer.'));
		reader.readAsText(blob);
	});
};

/** The filename the API named in Content-Disposition, else `<label>.csv`. */
export function confirmersFilename(headers, label) {
	const disposition = `${headers?.['content-disposition'] || ''}`;
	const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
	if (encoded) {
		try { return decodeURIComponent(encoded[1]); } catch (e) { /* the plain name below */ }
	}
	const plain = /filename="([^"]+)"/i.exec(disposition);
	if (plain) return plain[1];
	return `${slugify(label) || 'me-too'}.csv`;
}

/**
 * Who confirmed, as a file (managers only): `{ blob, filename, truncated,
 * totalCount, rowCount }`. A refusal is thrown with its JSON body read back out of the
 * blob it arrived as, so the caller words it by its `code` like any other.
 */
export async function exportConfirmers(itemId, element, { label } = {}) {
	try {
		const { data, headers } = await api.get(API_ITEM_CONFIRMERS_EXPORT_URL(itemId, element), { responseType: 'blob' });
		return {
			blob: data,
			filename: confirmersFilename(headers, label),
			truncated: `${headers?.['x-export-truncated'] || ''}`.toLowerCase() === 'true',
			totalCount: readTotalCount(headers, null),
			// How many people the file holds (`X-Export-Row-Count`).
			rowCount: Number.isFinite(Number(headers?.['x-export-row-count'])) ? Number(headers['x-export-row-count']) : null,
		};
	} catch (error) {
		const data = error?.response?.data;
		if (typeof Blob !== 'undefined' && data instanceof Blob) {
			try {
				error.response.data = JSON.parse(await readBlobText(data));
			} catch (parseError) {
				error.response.data = {};
			}
		}
		throw error;
	}
}
